/**
 * Self-update: an open app notices that a newer build has been deployed and moves onto it.
 *
 * <p>Why this exists (prod, 28.09.2026): a phone had the app open from before a deploy. Its JS was the
 * old one, the API was the new one, and the old code met a deposit phase it had never heard of — the
 * screen printed the raw i18n key and told the client the address had expired while their money was on
 * its way to the card. Nothing made an open tab (or an installed PWA brought back from the background)
 * pick up new code; the service worker's autoUpdate only swaps the files for the NEXT page load.</p>
 *
 * <p>The build bakes its own id in (`__APP_BUILD_ID__`) and publishes the same id as `/version.json`
 * (vite.config.ts). Comparing the two is one tiny no-cache request. On a mismatch the app reloads — but
 * only at a safe moment: never under someone typing into a form. Then it shows a banner instead and
 * lets the client choose.</p>
 *
 * <p>This file is the decision only, with every effect injected, so the rule is unit-tested without a
 * browser. The wiring lives in useAppUpdate.ts.</p>
 */

/**
 * How often an app that stays open and visible asks whether a new build is out. Five minutes: a deploy
 * reaches everyone who is actively using the app within that time, and the request is a few dozen bytes.
 * The app also checks whenever it comes back to the foreground (focus / visibilitychange) — that is the
 * case that matters on a phone, where the PWA sits in the background for hours.
 */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Foreground events can fire in bursts (focus + visibilitychange together); one check covers them. */
export const VERSION_CHECK_MIN_GAP_MS = 15 * 1000;

/** What the check decided. `unknown` = the server's id could not be read; nothing is done then. */
export type UpdateDecision = 'same' | 'reload' | 'banner' | 'unknown';

export interface VersionCheckDeps {
  /** This bundle's id. */
  currentId: string;
  /** The deployed build's id, or null when it cannot be read (offline, VPN gate, dev server fallback). */
  fetchServerId(): Promise<string | null>;
  /** Is the client in the middle of entering something that a reload would throw away? */
  isFormDirty(): boolean;
  /**
   * The id this tab already reloaded for, if any. Guards against a loop: if a reload did not bring the
   * new code (a stale service worker served the old shell), a second automatic reload would not either.
   */
  reloadedFor(): string | null;
  markReloadedFor(serverId: string): void;
  reload(): void | Promise<void>;
  showBanner(): void;
}

/** Runs one check and acts on it. */
export async function checkForUpdate(deps: VersionCheckDeps): Promise<UpdateDecision> {
  let serverId: string | null;
  try {
    serverId = await deps.fetchServerId();
  } catch {
    serverId = null;
  }
  if (!serverId) return 'unknown';
  if (serverId === deps.currentId) return 'same';

  // Already reloaded once for this very build and still on the old code: do not spin — ask instead.
  if (deps.isFormDirty() || deps.reloadedFor() === serverId) {
    deps.showBanner();
    return 'banner';
  }
  deps.markReloadedFor(serverId);
  await deps.reload();
  return 'reload';
}

/** Parses `/version.json`; anything that is not `{ "buildId": "<non-empty>" }` reads as "unknown". */
export function parseServerId(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const id = (body as { buildId?: unknown }).buildId;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : null;
}

const NON_TEXT_INPUTS = new Set(['hidden', 'button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image', 'range', 'color']);

function isEditable(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(el.type) && !el.readOnly && !el.disabled;
  return false;
}

/**
 * "Would a reload lose something the client typed?" — answered from the DOM, so no screen has to
 * register its forms. Dirty when any text field holds a value it was not rendered with (v-model sets the
 * value, not the attribute, so a pre-filled field counts too), or a focused contenteditable has text.
 * It errs towards "dirty": a false positive costs a banner tap, a false negative costs the client their
 * input. An EMPTY focused field is not dirty — the login screen autofocuses its email field and would
 * otherwise never update by itself.
 */
export function isFormDirty(doc: Document = document): boolean {
  const active = doc.activeElement;
  if (active instanceof HTMLElement && active.isContentEditable && (active.textContent ?? '').trim() !== '') return true;
  for (const el of Array.from(doc.querySelectorAll('input, textarea'))) {
    if (isEditable(el) && el.value !== el.defaultValue) return true;
  }
  return false;
}
